/* eslint-disable @typescript-eslint/no-unused-vars */
import { FieldInput } from '@/generated'
import { Icon } from '@/ui-legacy'
import { useEffect, useMemo } from 'react'
import { VegaLite } from 'react-vega'
import { useHierarchicalFilterContext } from '@/contexts/HierarchicalFilterContext/HierarchicalFilterContext'
import { usePlatformOutletContext } from '../Platform/usePlatFormOutletContext'
import { useVegaLiteChartLazyQuery } from './VegaLiteChart.generated'
import './VegaLiteChart.scss'
import { usePageFilters } from '@/contexts/PageFilterContext/PageFilterContextProvider2'

export type VegaLiteChartProps = {
  id: string
  tileFilterInputs?: FieldInput[]
}

function useVegaLiteChart(chartId: string, tileFilterInputs?: FieldInput[], pageFilterInputs?: FieldInput[] | null) {
  const { appId, appKey, projectId } = usePlatformOutletContext()
  const { hierarchicalFilterInputs } = useHierarchicalFilterContext()

  const [getChart, getChartData] = useVegaLiteChartLazyQuery()

  const filters = useMemo(() => {
    const _pageFilterInputs = pageFilterInputs ?? []
    const _tileFilterinputs = tileFilterInputs ?? []
    const _hierarchicalFilterinputs = hierarchicalFilterInputs ?? []
    return [..._tileFilterinputs, ..._pageFilterInputs, ..._hierarchicalFilterinputs]
  }, [tileFilterInputs, pageFilterInputs, hierarchicalFilterInputs])

  useEffect(() => {
    const fetchChart = async () => {
      getChart({
        fetchPolicy: 'network-only',
        variables: {
          appId: appKey,
          projectId,
          chartId,
          filters,
          extraInputs: [
            { name: 'appId', value: `${appId}` },
            { name: 'projectId', value: `${projectId}` },
          ],
        },
      })
    }

    fetchChart()
  }, [appId, appKey, chartId, filters, getChart, projectId])

  return {
    chart: getChartData.data?.app?.chartById,
    chartLoading: getChartData.loading,
  }
}

export function VegaLiteChart(props: VegaLiteChartProps) {
  const { pageFilterInputs } = usePageFilters()
  const { chart, chartLoading } = useVegaLiteChart(props.id, props.tileFilterInputs, pageFilterInputs)

  const classes = ['ux-vega-lite-chart'].filter(Boolean).join(' ')

  const { spec, config } = useMemo(() => {
    try {
      return {
        spec: JSON.parse(chart?.spec ?? ''),
        config: JSON.parse((chart?.config ?? '').replace('sans-serif', `'Helvetica Neue', sans-serif`)),
      }
    } catch (e) {
      return { spec: null, config: null }
    }
  }, [chart?.spec, chart?.config])

  if (chartLoading) {
    return <div>Loading...</div>
  }

  if (!chart?.spec || !chart?.config) {
    return (
      <div className="invalidChartSpec">
        <Icon icon="bar-chart-outline" />
      </div>
    )
  }

  return (
    <div className={'ux-vega-lite-chart-wrapper'}>
      <div className={classes}>
        <VegaLite
          spec={spec} // TODO could still be invalid...
          config={config} // TODO could still be invalid...
          {...({ ast: true } as Record<string, unknown>)}
        />
      </div>
    </div>
  )
}
